import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InboxIcon from "@mui/icons-material/Inbox";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { toast } from "react-toastify";
import api from "../../../conf/api";
import ApprovalToolbar from "./approvals/ApprovalToolbar";
import ApprovalCard from "./approvals/ApprovalCard";
import ApprovalsTable from "./approvals/ApprovalsTable";
import PreviewDialog from "./approvals/PreviewDialog";
import LogsDialog from "./approvals/LogsDialog";
import { getRowState } from "./approvals/constants";

const VIEW_STORAGE_KEY = "cv.pushAdminPortal.approvalsView";
const REFRESH_SECONDS = 30;

const readStoredView = () => {
  try {
    const v = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return v === "table" || v === "cards" ? v : "cards";
  } catch {
    // Private mode / blocked site data — the default view is fine.
    return "cards";
  }
};

function EmptyState({ icon, title, body, action }) {
  return (
    <Paper
      elevation={0}
      sx={{
        py: 7,
        px: 3,
        textAlign: "center",
        borderRadius: 3,
        border: "1px dashed #dbe3ec",
        bgcolor: "#fbfcfe",
      }}
    >
      <Box sx={{ color: "#cbd5e1", mb: 1, "& svg": { fontSize: 44 } }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={700} color="text.primary">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 420, mx: "auto" }}>
        {body}
      </Typography>
      {action && <Box mt={2}>{action}</Box>}
    </Paper>
  );
}

/**
 * Approvals list for AdminPortalMrssPush rows — mirrors QueuePreview.js's
 * approve/reject pattern for FeedPublishQueue.
 *
 * For video_source=EDITED, the edited-video render is submitted at push-submit
 * time (not deferred to approval — see admin_portal_push_views.py) so it can
 * be previewed and MUST finish before Approve is allowed — the backend
 * (AdminPortalPushApproveView) enforces this too; the disabled button here
 * is a UX courtesy, not the only gate.
 *
 * Row rendering lives in ./approvals/* — this file owns data, filtering and
 * the destructive-action dialogs only.
 */
export default function ApprovalsPanel() {
  const currentUser = useSelector((state) => state.user);
  const isAdmin = currentUser?.is_staff || currentUser?.is_superuser;

  const [filter, setFilter] = useState("PENDING_APPROVAL");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: "" });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, label: "" });
  const [previewRow, setPreviewRow] = useState(null);
  const [logsRow, setLogsRow] = useState(null);

  const [view, setView] = useState(readStoredView);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState("NEWEST");

  // Client-side filters, layered on top of the server-side approval_status
  // toggle — the endpoint already caps at 200 rows for that status, so
  // narrowing further (feed, source, push status, force, search) doesn't
  // need its own round trip.
  const [search, setSearch] = useState("");
  const [feedFilter, setFeedFilter] = useState("ALL");
  const [videoSourceFilter, setVideoSourceFilter] = useState("ALL");
  const [pushStatusFilter, setPushStatusFilter] = useState("ALL");
  const [forceFilter, setForceFilter] = useState("ALL");

  const changeView = (next) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Not being able to remember the preference isn't worth surfacing.
    }
  };

  // silent=true (auto-refresh ticks) skips the skeletons so a background poll
  // doesn't flash the whole list away every 30s — only the manual Refresh
  // button and the initial load show them.
  const fetchRows = useCallback((opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    setError(null);
    api
      .get("/mrss/admin-portal/push-status/", {
        params: filter === "ALL" ? {} : { status: filter },
      })
      .then((res) => {
        const data = res.data?.results ?? res.data ?? [];
        // Defensive: tolerate either a flat list, or a { unique_key: [...] }
        // keyed dict (the same endpoint also serves the video-badge lookup).
        const list = Array.isArray(data) ? data : Object.values(data).flat();
        setRows(list);
        if (res.data?.counts) setCounts(res.data.counts);
      })
      .catch(() => setError("Failed to load push approvals."))
      .finally(() => { if (!silent) setLoading(false); });
  }, [filter]);

  const fetchRowsRef = useRef(fetchRows);
  useEffect(() => {
    fetchRowsRef.current = fetchRows;
  }, [fetchRows]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Auto-refresh — same cadence as the backend's own render-status
  // poll (mrss_scheduler.py) — with a live countdown, mirroring
  // QueuePreview.js's ("Video Queue") auto-refresh so an EDITED-source row's
  // "Rendering…" chip flips to ready on its own instead of needing a click.
  const [countdown, setCountdown] = useState(REFRESH_SECONDS);
  useEffect(() => {
    setCountdown(REFRESH_SECONDS);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchRowsRef.current({ silent: true });
          return REFRESH_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [filter]);

  // Reset filters that reference values scoped to the previous approval-
  // status view (feed titles differ a lot between e.g. Approved and
  // Rejected) so a stale selection doesn't silently hide everything.
  useEffect(() => {
    setFeedFilter("ALL");
  }, [filter]);

  const feedOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const label = row.admin_portal_feed_title || row.admin_portal_feed_id;
      if (label) set.add(label);
    });
    return Array.from(set).sort();
  }, [rows]);

  const activeFilterCount =
    (feedFilter !== "ALL" ? 1 : 0) +
    (videoSourceFilter !== "ALL" ? 1 : 0) +
    (pushStatusFilter !== "ALL" ? 1 : 0) +
    (forceFilter !== "ALL" ? 1 : 0);

  const clearFilters = () => {
    setFeedFilter("ALL");
    setVideoSourceFilter("ALL");
    setPushStatusFilter("ALL");
    setForceFilter("ALL");
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = rows.filter((row) => {
      if (q) {
        const haystack = `${row.video_title || ""} ${row.unique_key || ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (feedFilter !== "ALL" && (row.admin_portal_feed_title || row.admin_portal_feed_id) !== feedFilter) {
        return false;
      }
      if (videoSourceFilter !== "ALL" && (row.video_source || "ORIGINAL") !== videoSourceFilter) {
        return false;
      }
      if (pushStatusFilter !== "ALL" && row.status !== pushStatusFilter) {
        return false;
      }
      if (forceFilter === "FORCE" && !row.is_force_push) return false;
      if (forceFilter === "NORMAL" && row.is_force_push) return false;
      return true;
    });

    const time = (r) => (r.created_date ? new Date(r.created_date).getTime() : 0);
    if (sort === "OLDEST") return [...out].sort((a, b) => time(a) - time(b));
    if (sort === "TITLE") {
      return [...out].sort((a, b) =>
        (a.video_title || a.unique_key || "").localeCompare(b.video_title || b.unique_key || "")
      );
    }
    return [...out].sort((a, b) => time(b) - time(a)); // NEWEST — also the server's own order
  }, [rows, search, feedFilter, videoSourceFilter, pushStatusFilter, forceFilter, sort]);

  // Only ever counted over what's currently in view, so the banner can't
  // claim failures the user has filtered away.
  const failedCount = useMemo(() => filteredRows.filter((r) => r.status === "FAILED").length, [filteredRows]);
  const staleCount = useMemo(
    () => filteredRows.filter((r) => getRowState(r, isAdmin).isStale).length,
    [filteredRows, isAdmin]
  );

  const handleApprove = async (id, thumbnailBase64) => {
    setBusyId(id);
    try {
      const payload = thumbnailBase64 ? { thumbnail_base64: thumbnailBase64 } : {};
      await api.post(`/mrss/admin-portal/push/${id}/approve/`, payload);
      toast.success(thumbnailBase64 ? "Approved with custom thumbnail" : "Approved — will be sent to Admin Portal shortly");
      if (previewRow?.id === id) setPreviewRow(null);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to approve");
    } finally {
      setBusyId(null);
    }
  };

  const handleRetry = async (id, allowDuplicate) => {
    setBusyId(id);
    try {
      await api.post(`/mrss/admin-portal/push/${id}/retry/`, allowDuplicate ? { allow_duplicate: true } : {});
      toast.success("Re-queued — will be retried on the next scheduler tick");
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to retry");
    } finally {
      setBusyId(null);
    }
  };

  const handleRejectConfirm = async () => {
    setBusyId(rejectDialog.id);
    try {
      await api.post(`/mrss/admin-portal/push/${rejectDialog.id}/reject/`, { reason: rejectDialog.reason });
      toast.success("Push rejected");
      setRejectDialog({ open: false, id: null, reason: "" });
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reject");
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    const id = deleteDialog.id;
    setBusyId(id);
    try {
      const { data } = await api.delete(`/mrss/admin-portal/push/${id}/delete/`);
      const n = data?.deleted_files?.length || 0;
      toast.success(n ? `Push deleted — ${n} file(s) removed` : "Push deleted");
      setDeleteDialog({ open: false, id: null, label: "" });
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  const actions = {
    onPreview: (row) => setPreviewRow(row),
    onLogs: (row) => setLogsRow(row),
    onApprove: handleApprove,
    onReject: (id) => setRejectDialog({ open: true, id, reason: "" }),
    onRetry: handleRetry,
    onDelete: (row) =>
      setDeleteDialog({
        open: true,
        id: row.id,
        label: `${row.video_title || row.unique_key} → ${row.admin_portal_feed_title || row.admin_portal_feed_id}`,
      }),
  };

  // Re-read the open rows from `rows` so the dialogs keep updating as the 30s
  // auto-refresh advances a push, rather than freezing on the snapshot taken
  // when they were opened.
  const livePreviewRow = previewRow ? rows.find((r) => r.id === previewRow.id) || previewRow : null;
  const liveLogsRow = logsRow ? rows.find((r) => r.id === logsRow.id) || logsRow : null;

  return (
    <Box>
      <ApprovalToolbar
        filter={filter}
        onFilterChange={setFilter}
        counts={counts}
        search={search}
        onSearchChange={setSearch}
        feedFilter={feedFilter}
        onFeedFilterChange={setFeedFilter}
        feedOptions={feedOptions}
        videoSourceFilter={videoSourceFilter}
        onVideoSourceFilterChange={setVideoSourceFilter}
        pushStatusFilter={pushStatusFilter}
        onPushStatusFilterChange={setPushStatusFilter}
        forceFilter={forceFilter}
        onForceFilterChange={setForceFilter}
        sort={sort}
        onSortChange={setSort}
        view={view}
        onViewChange={changeView}
        filtersOpen={filtersOpen}
        onFiltersOpenChange={setFiltersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
        shown={filteredRows.length}
        total={rows.length}
        countdown={countdown}
        loading={loading}
        onRefresh={() => fetchRows()}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Only admins can approve or reject pushes. You can still preview and check status here.
        </Alert>
      )}

      {!loading && (failedCount > 0 || staleCount > 0) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 2,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: failedCount > 0 ? "#fff7ed" : "#fffbeb",
            border: `1px solid ${failedCount > 0 ? "#fed7aa" : "#fde68a"}`,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 18, color: "#b45309" }} />
          <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 600 }}>
            Needs attention
          </Typography>
          {failedCount > 0 && (
            <Chip
              size="small"
              clickable
              onClick={() => { setPushStatusFilter("FAILED"); setFiltersOpen(true); }}
              label={`${failedCount} failed push${failedCount !== 1 ? "es" : ""} — retryable`}
              sx={{ height: 22, fontSize: 11, fontWeight: 600, color: "#b91c1c", bgcolor: "#fef2f2" }}
            />
          )}
          {staleCount > 0 && (
            <Chip
              size="small"
              label={`${staleCount} waiting over 2 days`}
              sx={{ height: 22, fontSize: 11, fontWeight: 600, color: "#92400e", bgcolor: "#fef3c7" }}
            />
          )}
        </Box>
      )}

      {loading ? (
        view === "cards" ? (
          <Grid container spacing={2}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={52} sx={{ mb: 0.75, borderRadius: 2 }} />
            ))}
          </Box>
        )
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<InboxIcon />}
          title="Nothing here yet"
          body={
            filter === "PENDING_APPROVAL"
              ? "No pushes are waiting on a decision. Submit videos from the New Push tab and they'll queue up here for approval."
              : "No pushes have this status yet."
          }
        />
      ) : filteredRows.length === 0 ? (
        <EmptyState
          icon={<FilterAltOffIcon />}
          title="No pushes match these filters"
          body={`${rows.length} push${rows.length !== 1 ? "es" : ""} in this view, none matching the current search and filters.`}
          action={
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => { clearFilters(); setSearch(""); }}
              sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0" }}
            >
              Clear search and filters
            </Button>
          }
        />
      ) : view === "cards" ? (
        <Grid container spacing={2}>
          {filteredRows.map((row) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={row.id}>
              <ApprovalCard row={row} isAdmin={isAdmin} busyId={busyId} actions={actions} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <ApprovalsTable rows={filteredRows} isAdmin={isAdmin} busyId={busyId} actions={actions} />
      )}

      <PreviewDialog
        row={livePreviewRow}
        isAdmin={isAdmin}
        busyId={busyId}
        onClose={() => setPreviewRow(null)}
        onApprove={handleApprove}
        onReject={(id) => setRejectDialog({ open: true, id, reason: "" })}
        onRetry={handleRetry}
      />

      <LogsDialog row={liveLogsRow} onClose={() => setLogsRow(null)} />

      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, id: null, reason: "" })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Reject this push?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Nothing has reached Admin Portal yet, so rejecting is safe. Any file this push uploaded
            is removed at the same time.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="Reason for rejection (optional)"
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRejectDialog({ open: false, id: null, reason: "" })}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disableElevation
            startIcon={<ThumbDownIcon />}
            disabled={busyId === rejectDialog.id}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, label: "" })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete this push?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1.5} fontWeight={600}>
            {deleteDialog.label}
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            This removes the push record and any files it still holds — the staged upload on S3
            and any local copy. This can't be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, id: null, label: "" })}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disableElevation
            startIcon={<DeleteForeverIcon />}
            disabled={busyId === deleteDialog.id}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
